import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";

const SLICK_ARROW_LEFT = "<button type=\"button\" class=\"slick-prev slick-arrow pill\"><i aria-hidden=\"true\" class=\"icon-lasso-chevron-left\"></i></button>";
const SLICK_ARROW_RIGHT = "<button type=\"button\" class=\"slick-next slick-arrow pill\"><i aria-hidden=\"true\" class=\"icon-lasso-chevron-right\"></i></button>";

const LOADING_PRODUCT_DETAIL_HTML = `
  <div class="product-details-loading">
    <div class="loading-images-column">
      <div class="loading-thumbs">
        <div class="loading-thumb product-modal-shimmer"></div>
        <div class="loading-thumb product-modal-shimmer"></div>
        <div class="loading-thumb product-modal-shimmer"></div>
      </div>
      <div class="loading-fullsize product-modal-shimmer"></div>
    </div>
    <div class="loading-info-column">
      <div class="loading-title product-modal-shimmer"></div>
      <div class="loading-tags product-modal-shimmer"></div>
      <div class="loading-summary product-modal-shimmer"></div>
      <div class="loading-line product-modal-shimmer"></div>
      <div class="loading-select product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
    </div>
  </div>
`;

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 992;

// Owns both the product grid with its infinite scroll and the shared #product-detail-modal
// carousel. collection_generator_products_controller.js and give_collection_controller.js carry
// their own equivalents rather than sharing one module.
//
// R9: the grid and the modal/carousel are two candidate responsibilities to split apart, but the
// modal element (#product-detail-modal) is not wrapped in its own data-controller in markup -- it
// sits inside this same mount (gives/collections/previews/show.html.haml), rendered by the shared
// gives/shared/_product_details_carousel_modal partial. Splitting it into its own controller would
// mean adding a second `data-controller` attribute to that partial, which is out of reach (see
// connect() below). Deferred, same as lane 9/lane 10's R9 calls when the seam called back into
// markup outside the lane's reach.
export default class extends Controller {
  static get targets() {
    return ["productTemplate", "totalProducts"];
  }

  static get values() {
    return { productsUrl: String, collectionId: String };
  }

  static get classes() {
    return ["collapsed"];
  }

  connect() {
    this.products = [];
    this.loading = false;
    this.currentPage = 0;
    this.totalPages = 0;
    this.currentProductIndex = 0;

    // These stay plain lookups, not Targets: this mount's own view
    // (gives/collections/previews/show.html.haml) and everything it renders
    // (gives/shared/_product_columns, gives/shared/_product_details_carousel_modal,
    // gives/collections/previews/_{featured,standard}_collection_details) live under
    // app/views/gives/, the give-flow tree owned by the pjax->Turbo migration lane -- adding
    // Target/data-action markup there is out of scope here regardless of sharing. _product_columns
    // is also rendered by gives/collection_generator/index.html.haml
    // (collection_generator_products_controller.js, see bd9cdb8320) and
    // _product_details_carousel_modal by that same view plus subdomain/collections/show.html.haml,
    // so even absent the give-flow boundary these would be D12 shared-partial skips too.
    this.desktopGrid = this.element.querySelector(".desktop-products-grid");
    this.mobileGrid = this.element.querySelector(".mobile-products-grid");
    this.modalEl = this.element.querySelector("#product-detail-modal");
    this.carouselEl = this.modalEl ? this.modalEl.querySelector(".product-details-carousel") : null;

    // Same reasoning as above: `.collection-detail-caret` and `.custom-packaging-card` come from
    // _standard_collection_details/_featured_collection_details and _custom_packaging_options,
    // all under gives/collections/previews/ -- out of reach here. The class name itself is on the
    // Classes API now (`static classes`, above) with a same-value fallback below, so no markup
    // edit is required for this call site's behavior to stay identical.
    this.collapsibleEls().forEach((el) => el.classList.add(this.collapsedClassName));

    // The scroll listener stays manual: it's bound partly to `window`, which a data-action can't
    // reach, and partly to `desktopGrid`, the shared-partial markup noted above. Both media-query
    // listeners are on MediaQueryList objects, which have no markup element to hang a data-action
    // off at all.
    this.scrollHandler = this.handleScroll.bind(this);
    this.refreshSlickHandler = this.refreshSlick.bind(this);
    if (this.desktopGrid) { this.desktopGrid.addEventListener("scroll", this.scrollHandler); }
    window.addEventListener("scroll", this.scrollHandler);

    this.mobileMediaQuery = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px)`);
    this.tabletMediaQuery = window.matchMedia(`(min-width: ${TABLET_BREAKPOINT}px)`);
    this.mobileMediaQuery.addEventListener("change", this.refreshSlickHandler);
    this.tabletMediaQuery.addEventListener("change", this.refreshSlickHandler);

    // Bootstrap's modal plugin announces `shown`/`hidden` through jQuery's event system only, and
    // everything downstream in this file that touches the carousel is slick-owned markup
    // (`.slick-slide`, `.slick-cloned`, `.slick-active` are classes the plugin itself applies, not
    // anything our own views declare), so the jQuery wrapper stays for the whole modal/carousel
    // lifecycle below (open, teardown, and the slide contents) rather than converting only some of
    // these calls to native.
    if (this.modalEl) {
      $(this.modalEl).on("shown.bs.modal.collectionDetailProducts", () => this.onModalShown());
      $(this.modalEl).on("hidden.bs.modal.collectionDetailProducts", () => this.onModalHidden());
    }

    this.loadPage(1);
  }

  disconnect() {
    if (this.desktopGrid) { this.desktopGrid.removeEventListener("scroll", this.scrollHandler); }
    window.removeEventListener("scroll", this.scrollHandler);
    if (this.mobileMediaQuery) { this.mobileMediaQuery.removeEventListener("change", this.refreshSlickHandler); }
    if (this.tabletMediaQuery) { this.tabletMediaQuery.removeEventListener("change", this.refreshSlickHandler); }
    if (this.modalEl) { $(this.modalEl).off(".collectionDetailProducts"); }
    if (this.carouselEl && $(this.carouselEl).hasClass("slick-initialized")) {
      $(this.carouselEl).off("afterChange");
      $(this.carouselEl).slick("unslick");
    }
  }

  // `data-collection-detail-products-collapsed-class` is never set in the give-flow markup this
  // mounts on (out of reach, see connect() above), so this always falls back to the literal
  // "collapsed" it replaces -- same class name, just declared through the Classes API instead of
  // hard-coded at each call site, so a future markup change can override it without a JS edit.
  get collapsedClassName() {
    return this.hasCollapsedClass ? this.collapsedClass : "collapsed";
  }

  collapsibleEls() {
    return Array.from(this.element.querySelectorAll(".collection-detail-caret, .custom-packaging-card"));
  }

  // Expand or collapse the CPB details in mobile
  toggleCollapse(event) {
    const isCollapsed = event.currentTarget.classList.contains(this.collapsedClassName);
    this.collapsibleEls().forEach((el) => el.classList.toggle(this.collapsedClassName, !isCollapsed));
  }

  // Used natively to start the SAG flow. `#giving-form` is unique page-wide (rendered once, inside
  // this same mount by _collection_detail_panel.html.haml) and carries no unobtrusive-validation
  // plugin of its own, so a plain native submit is equivalent to the jQuery call it replaces.
  nextStep(event) {
    event.preventDefault();
    document.getElementById("giving-form").submit();
  }

  /* Products grid load */
  loadPage(page) {
    this.loading = true;
    // No loading-card placeholders: they have never rendered here. The old implementation looked
    // up a template path that did not exist, so its append was always a no-op.

    const url = `${this.productsUrlValue}?page=${page}`;

    fetch(url, RequestHelper.defaultRequestOptions())
      .then((response) => {
        if (!response.ok) {
          return response.json()
            .catch(() => ({}))
            .then((body) => { throw new Error(body?.error || "Failed to load collection products."); });
        }
        return response.json();
      })
      .then((data) => {
        this.currentPage = data.pagination.current_page;
        this.totalPages = data.pagination.total_pages;
        this.totalProductsTargets.forEach((el) => { el.textContent = data.collection.total_products; });

        const products = this.addApiProducts(data.products);
        this.products = page === 1 ? products : this.products.concat(products);
      })
      .catch((error) => {
        window.ltAlert({ message: error.message || "Failed to load collection products.", type: "toast-error", duration: 2500 });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  addApiProducts(products) {
    const startIndex = this.products.length;
    const desktopNodes = products.map((p, i) => this.buildProductCard(p, `product-${p.id}-desktop`, startIndex + i));
    const mobileNodes = products.map((p, i) => this.buildProductCard(p, `product-${p.id}-mobile`, startIndex + i));
    this.addToColumns(this.desktopGrid, desktopNodes, startIndex, 3);
    this.addToColumns(this.mobileGrid, mobileNodes, startIndex, 2);
    return products;
  }

  addToColumns(grid, nodes, startIndex, columnCount) {
    if (!grid) { return; }
    const columns = grid.querySelectorAll(".generated-products-column");
    nodes.forEach((node, i) => {
      const column = columns[(startIndex + i) % columnCount];
      if (column) { column.appendChild(node); }
    });
  }

  buildProductCard(product, containerId, productIndex) {
    const fragment = this.productTemplateTarget.content.cloneNode(true);
    const el = fragment.firstElementChild;
    el.id = containerId;
    // Read back below off `event.currentTarget`, not this controller's own root element -- the
    // Values API only expresses attributes on the controller's mount, not a per-card index on a
    // node the controller creates itself, so this stays a plain attribute.
    el.dataset.productIndex = productIndex;
    el.querySelector(".product-name").textContent = product.name;

    const carousel = el.querySelector(".carousel");
    (product.images || []).forEach((src) => {
      const imageEl = document.createElement("div");
      imageEl.className = "image";
      const img = document.createElement("img");
      img.className = "thumbnail-1";
      img.src = src;
      imageEl.appendChild(img);
      carousel.appendChild(imageEl);
    });

    // After the element is linked and in the DOM, initialize the carousel if there are
    // multiple images -- deferred a frame so `addToColumns` (called by our caller right after
    // this returns) has appended it first, the same ordering the original got for free from
    // `$timeout(fn, 0)` running after the current digest had already inserted the compiled node.
    if (product.images && product.images.length > 1) {
      requestAnimationFrame(() => this.initProductCarousel(el));
    }

    return el;
  }

  initProductCarousel(cardEl) {
    const $carousel = $(cardEl).find(".carousel");
    if ($carousel.length && $carousel.children().length > 0 && !$carousel.hasClass("slick-initialized")) {
      $carousel.slick({
        dots: false,
        infinite: true,
        speed: 300,
        slidesToShow: 1,
        adaptiveHeight: false,
        prevArrow: SLICK_ARROW_LEFT,
        nextArrow: SLICK_ARROW_RIGHT
      });
    }
  }

  // Shows the product details inside a modal
  expandProductDetails(event) {
    if (event.target.closest(".slick-arrow")) { return; }
    const index = parseInt(event.currentTarget.dataset.productIndex, 10);
    this.openModal(index);
  }

  openModal(index) {
    this.currentProductIndex = isNaN(index) ? 0 : index;
    if (this.modalEl) { $(this.modalEl).modal("show"); }
  }

  // When the modal shows, load the carousel and fetch the product details
  onModalShown() {
    const $carousel = $(this.carouselEl);
    $carousel.empty();
    this.products.forEach(() => $carousel.append("<div class=\"item\"></div>"));

    $carousel.slick({
      infinite: false,
      initialSlide: this.currentProductIndex,
      arrows: true,
      nextArrow: SLICK_ARROW_RIGHT,
      prevArrow: SLICK_ARROW_LEFT,
      responsive: [
        {
          breakpoint: 768,
          settings: { arrows: false }
        }
      ]
    });

    // When the carousel changes, load the other slide content.
    // Rebound on every open since it's unbound in `onModalHidden` below; `.off` first guards
    // against double-binding if this ever runs twice per open.
    $carousel.off("afterChange").on("afterChange", (event, slick, currentSlide) => {
      this.currentProductIndex = currentSlide;

      this.insertProductDetails();

      if (this.currentProductIndex >= this.products.length - 3 && this.currentPage < this.totalPages) {
        this.loadPage(this.currentPage + 1);
      }
    });

    this.insertProductDetails();
  }

  // Resets the carousel when the modal is hidden
  onModalHidden() {
    const $carousel = $(this.carouselEl);
    if ($carousel.hasClass("slick-initialized")) {
      $carousel.off("afterChange");
      $carousel.slick("unslick");
    }
  }

  insertProductDetails(productIndex = this.currentProductIndex) {
    const $slide = $(this.carouselEl).find(".slick-slide").not(".slick-cloned").eq(productIndex);
    this.fetchProductDetailIntoSlide($slide, this.products[productIndex]);
  }

  // Fetch the product from the BE and insert the HTML into the slide
  fetchProductDetailIntoSlide($slide, product) {
    if (!product || $slide.data("loaded")) {
      return;
    }

    // Both the loading placeholder below (a fixed constant, no interpolated content) and the
    // fetched `html` two lines down are safe to write as markup. The placeholder is a literal
    // template-string constant declared at the top of this file (LOADING_PRODUCT_DETAIL_HTML),
    // never built from product or user data. The fetched HTML is server-rendered by our own app,
    // same-origin, from Gives::Collections::ProductsController#show rendering
    // gives/collections/products/_modal_body.html.haml: every field it outputs (@product.name,
    // .summary, .description, variant names) uses plain HAML `=` (escaped) except .summary and
    // .description, which go through ProductDecorator#summary/#description ->
    // ProductsHelper#sanitized_html, which runs Rails' own `sanitize` (Rails::Html::
    // WhiteListSanitizer) rather than trusting the method name -- confirmed by reading it, not
    // assuming it (see D22). No `raw`/`!=` anywhere in the partial.
    $slide.html(LOADING_PRODUCT_DETAIL_HTML);

    const url = `/give/collections/${this.collectionIdValue}/products/${product.id}?cta=products_grid`;

    fetch(url, RequestHelper.defaultRequestOptions())
      .then((response) => response.text())
      .then((html) => {
        $slide.empty().append(html);
        this.wireProductDetail($slide);
        $slide.data("loaded", true);
      })
      .catch((error) => console.warn("Failed to load product detail into slide", error)); // eslint-disable-line no-console
  }

  // `gives/collections/products/_modal_body` is shared with the give-collection and generator
  // views and carries no data-action of its own, so its controls are wired up by hand here.
  wireProductDetail($slide) {
    $slide.find(".thumbs a").on("click", (event) => this.scrollToCorrespondingImg(event));
    $slide.find(".variants select").on("change", (event) => this.changeVariant(event.target.value));
  }

  /* Product details modal - scrolling */

  // Scroll when a variant is selected, it must scroll the
  // thumbnails and the full size images
  changeVariant(id) {
    const isDesktop = window.innerWidth > 1130;

    const variant = isDesktop ? "desktop" : "mobile";
    const scrollAttr = isDesktop ? "scrollTop" : "scrollLeft";
    const prop = isDesktop ? "top" : "left";

    const $modal = $(this.modalEl);
    const $thumbnails = $modal.find(`.slick-active .product-details-${variant} .thumbs`);
    const $fullSizeImages = $modal.find(`.slick-active .product-details-${variant} .fullsize`);

    [$thumbnails, $fullSizeImages].forEach((container) => {
      if (!container.length) {
        return;
      }

      const img = container.find(`img[data-id="${id}"]`)[0];

      if (img) {
        container[0][scrollAttr] =
          $(img).offset()[prop] - container.offset()[prop] + container[0][scrollAttr];
      } else {
        container[0][scrollAttr] = 0;
      }
    });

    $thumbnails.find("a").removeClass("active");
    const activeThumb = $thumbnails.find(`img[data-id="${id}"]`)[0];

    if (activeThumb) {
      $(activeThumb).parent().addClass("active");
    } else {
      $thumbnails.find("a:first").addClass("active");
    }
  }

  // Scroll the full size images when a thumbnail is selected
  scrollToCorrespondingImg(event) {
    event.preventDefault();

    const isDesktop = window.innerWidth > 1130;
    const src = $(event.target).attr("src").split("tile")[0];

    const variant = isDesktop ? "desktop" : "mobile";
    const scrollAttr = isDesktop ? "scrollTop" : "scrollLeft";
    const prop = isDesktop ? "top" : "left";

    const $modal = $(this.modalEl);
    const $thumbnails = $modal.find(`.slick-active .product-details-${variant} .thumbs`);
    const $fullSizeImages = $modal.find(`.slick-active .product-details-${variant} .fullsize`);
    const img = $fullSizeImages.find(`img[src^="${src}"]`)[0];

    $thumbnails.find("a").removeClass("active");
    $(event.target).parent().addClass("active");

    if (!$fullSizeImages.length) {
      return;
    }

    if (img) {
      $fullSizeImages[0][scrollAttr] =
        $(img).offset()[prop] - $fullSizeImages.offset()[prop] + $fullSizeImages[0][scrollAttr];
    } else {
      $fullSizeImages[0][scrollAttr] = 0;
    }
  }

  handleScroll() {
    if (!this.desktopGrid) { return; }

    let scrollTop = this.desktopGrid.scrollTop;
    let scrollHeight = this.desktopGrid.scrollHeight;
    let clientHeight = this.desktopGrid.offsetHeight;

    if (scrollTop === 0 && window.scrollY > 0) {
      scrollTop = window.scrollY;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    }

    if (this.loading || this.currentPage >= this.totalPages) { return; }

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      this.loadPage(this.currentPage + 1);
    }
  }

  refreshSlick() {
    this.element.querySelectorAll(".carousel.slick-initialized").forEach((el) => $(el).slick("refresh"));
  }
}
